import { useEffect, RefObject } from 'react';

/**
 * Hook detecting clicks outside a referenced element.
 * Useful for closing dropdowns, modals, and menus.
 *
 * @param ref - Reference to the element to monitor
 * @param handler - Callback invoked when click occurs outside the element
 * @param enabled - Optional flag to enable/disable the listener (default: true)
 *
 * @example
 * const dropdownRef = useRef<HTMLDivElement>(null);
 * useClickOutside(dropdownRef, () => setIsOpen(false), isOpen);
 */
export const useClickOutside = (
  ref: RefObject<HTMLElement | null>,
  handler: () => void,
  enabled: boolean = true
) => {
  useEffect(() => {
    if (!enabled) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handler();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref, handler, enabled]);
};
